/**
 * ProductEditPage — Angular `ProductEditComponent` parity: Basic Info, Risk Level, Communication Templates.
 * UI matches product module edit screenshots: header actions, EntityEditTabBar, single form card for basic info.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ProductEditBasicFormValues, ProductEditTab } from '../types'
import { productEditBasicFormSchema } from '../types/schemas'
import { Loader2, AlertCircle } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EntityEditTabBar, type EntityEditTabItem } from '@/components/ui/EntityEditTabBar'
import { useUserRole } from '@/hooks/useUserRole'
import { showSuccess, showProductApiError, showInfo } from '@/lib/utils/notifications'
import { to01 } from '../utils/productRiskMatrixModel'
import { DESCRIPTION_MAX, PRODUCT_POLICIES } from '../constants'
import { productConfirmPresentation } from '../constants/productDialogProps'
import { fetchProductDetail, updateProduct } from '../services/productApi'
import type { ProductListItem } from '../types'
import {
  ProductRiskLevelTab,
  type ProductRiskLevelTabHandle,
} from '../components/ProductRiskLevelTab'
import {
  ProductCommunicationTemplatesTab,
  type ProductCommunicationTemplatesTabHandle,
} from '../components/ProductCommunicationTemplatesTab'
import { cn } from '@/lib/utils/helpers'
import styles from '../styles/productAdd.module.css'

type FormValues = ProductEditBasicFormValues
type Tab = ProductEditTab

function snapshotFromValues(v: FormValues) {
  return {
    name: String(v.name ?? ''),
    description: String(v.description ?? ''),
    long_description: String(v.long_description ?? ''),
    allow_dynamic_access_fee: to01(v.allow_dynamic_access_fee),
    is_active: to01(v.is_active),
  }
}

export function ProductEditPage() {
  const { productId: productIdParam } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const { hasPolicy } = useUserRole()
  const canEdit = hasPolicy([PRODUCT_POLICIES.EDIT])

  const productId = parseInt(productIdParam || '', 10)
  const [loading, setLoading] = useState(true)
  const [productData, setProductData] = useState<(ProductListItem & Record<string, unknown>) | null>(null)
  const [selectedTab, setSelectedTab] = useState<Tab>('basicinfo')
  const [baseline, setBaseline] = useState<ReturnType<typeof snapshotFromValues> | null>(null)
  const [tabDialog, setTabDialog] = useState<{ open: boolean; target: Tab | null }>({ open: false, target: null })
  const pendingTabAfterSaveRef = useRef<Tab | null>(null)

  const riskRef = useRef<ProductRiskLevelTabHandle>(null)
  const commRef = useRef<ProductCommunicationTemplatesTabHandle>(null)
  const [riskUi, setRiskUi] = useState({ dirty: false, locked: true })
  const [commDirty, setCommDirty] = useState(false)
  /** Latches true on first Communication Templates open so the pane stays mounted afterwards (M-09). */
  const [commMounted, setCommMounted] = useState(false)
  /** In-flight save lock — prevents duplicate submits/POSTs on double-click (M-10). */
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const beginSubmit = () => {
    submittingRef.current = true
    setSubmitting(true)
  }
  const endSubmit = () => {
    submittingRef.current = false
    setSubmitting(false)
  }
  const lastFeeRef = useRef<number | null>(null)
  const handleRiskError = useCallback((msg: string) => {
    showProductApiError(msg)
  }, [])
  const handleCommError = useCallback((msg: string) => {
    showProductApiError(msg)
  }, [])

  const form = useForm<FormValues>({
    resolver: zodResolver(productEditBasicFormSchema),
    defaultValues: {
      name: '',
      description: '',
      long_description: '',
      allow_dynamic_access_fee: 0,
      is_active: 1,
    },
    mode: 'onChange',
  })

  const isBase = productData?.product_id === 1

  const canShowRisk = !isBase && to01(form.watch('allow_dynamic_access_fee')) === 1
  const canShowComm = !!productId && !Number.isNaN(productId)

  const watchedValues = form.watch()
  const descriptionLen = (watchedValues.description ?? '').length

  const basicDirty = useMemo(() => {
    if (!baseline) return false
    return JSON.stringify(snapshotFromValues(watchedValues)) !== JSON.stringify(baseline)
  }, [watchedValues, baseline])

  const productEditTabs = useMemo<EntityEditTabItem[]>(() => {
    const tabs: EntityEditTabItem[] = [
      { id: 'basicinfo', label: 'Basic info', testId: 'product-edit-tab-basicinfo' },
    ]
    if (canShowRisk) {
      tabs.push({ id: 'risklevel', label: 'Risk Level', testId: 'product-edit-tab-risklevel' })
    }
    if (canShowComm) {
      tabs.push({
        id: 'communicationtemplates',
        label: 'Communication templates',
        testId: 'product-edit-tab-communicationtemplates',
      })
    }
    return tabs
  }, [canShowRisk, canShowComm])

  const loadProduct = useCallback(async () => {
    if (!canEdit) return
    if (!productId || Number.isNaN(productId)) {
      navigate('/product', { replace: true })
      return
    }
    setLoading(true)
    try {
      const data = await fetchProductDetail(productId)
      setProductData(data)
      const v: FormValues = {
        name: String(data.name ?? ''),
        description: String(data.description ?? ''),
        long_description: String(data.long_description ?? ''),
        allow_dynamic_access_fee: to01(data.allow_dynamic_access_fee) as 0 | 1,
        is_active: to01(data.is_active) as 0 | 1,
      }
      form.reset(v)
      setBaseline(snapshotFromValues(v))
      lastFeeRef.current = to01(data.allow_dynamic_access_fee)
    } catch (e) {
      showProductApiError(e)
      navigate('/product', { replace: true })
    } finally {
      setLoading(false)
    }
  }, [productId, navigate, form, canEdit])

  useEffect(() => {
    void loadProduct()
  }, [loadProduct])

  useEffect(() => {
    if (selectedTab === 'risklevel' && !canShowRisk) setSelectedTab('basicinfo')
    if (selectedTab === 'communicationtemplates' && !canShowComm) setSelectedTab('basicinfo')
  }, [selectedTab, canShowRisk, canShowComm])

  const currentTabDirty = () => {
    if (selectedTab === 'basicinfo') return basicDirty
    if (selectedTab === 'risklevel') return riskRef.current?.isDirty() ?? false
    if (selectedTab === 'communicationtemplates') return commRef.current?.isDirty() ?? false
    return false
  }

  const tabLabel = (t: Tab) => {
    if (t === 'basicinfo') return 'Basic Info'
    if (t === 'risklevel') return 'Risk Level'
    return 'Communication Templates'
  }

  const activateTab = (t: Tab) => {
    setSelectedTab(t)
  }

  const requestTab = (target: Tab) => {
    if (target === selectedTab) return
    if (target === 'risklevel' && !canShowRisk) return
    if (target === 'communicationtemplates' && !canShowComm) return
    if (!currentTabDirty()) {
      activateTab(target)
      return
    }
    setTabDialog({ open: true, target })
  }

  const discardCurrentTab = () => {
    if (selectedTab === 'basicinfo' && productData) {
      const v: FormValues = {
        name: String(productData.name ?? ''),
        description: String(productData.description ?? ''),
        long_description: String(productData.long_description ?? ''),
        allow_dynamic_access_fee: to01(productData.allow_dynamic_access_fee) as 0 | 1,
        is_active: to01(productData.is_active) as 0 | 1,
      }
      form.reset(v)
      setBaseline(snapshotFromValues(v))
      lastFeeRef.current = to01(productData.allow_dynamic_access_fee)
    }
    if (selectedTab === 'risklevel') riskRef.current?.cancel()
    if (selectedTab === 'communicationtemplates') commRef.current?.discard()
  }

  const saveCurrentAndGo = async (target: Tab) => {
    if (submittingRef.current) return
    beginSubmit()
    try {
      pendingTabAfterSaveRef.current = target
      if (selectedTab === 'basicinfo') {
        await submitBasicInfo()
        return
      }
      if (selectedTab === 'risklevel') {
        const ok = await riskRef.current?.save()
        pendingTabAfterSaveRef.current = null
        if (ok) activateTab(target)
        return
      }
      if (selectedTab === 'communicationtemplates') {
        const ok = await commRef.current?.save()
        pendingTabAfterSaveRef.current = null
        if (ok) activateTab(target)
      }
    } finally {
      endSubmit()
    }
  }

  const submitBasicInfo = form.handleSubmit(
    async (data) => {
    if (!productId || Number.isNaN(productId)) return
    const tabSwitch = pendingTabAfterSaveRef.current
    pendingTabAfterSaveRef.current = null
    try {
      await updateProduct(productId, {
        // Angular sends name/description raw (untrimmed) — mirror that here.
        name: data.name,
        description: data.description ?? '',
        long_description: data.long_description ?? '',
        allow_dynamic_access_fee: (isBase
          ? to01(productData?.allow_dynamic_access_fee)
          : to01(data.allow_dynamic_access_fee)) as 0 | 1,
        is_active: (isBase ? to01(productData?.is_active) : to01(data.is_active)) as 0 | 1,
      })
      showSuccess('Product updated successfully!')
      const fee = isBase ? to01(productData?.allow_dynamic_access_fee) : to01(data.allow_dynamic_access_fee)
      const active = isBase ? to01(productData?.is_active) : to01(data.is_active)
      const snap = snapshotFromValues({
        ...data,
        allow_dynamic_access_fee: fee,
        is_active: active,
      } as FormValues)
      setBaseline(snap)
      form.reset({
        ...data,
        allow_dynamic_access_fee: fee as 0 | 1,
        is_active: active as 0 | 1,
      })
      // Angular re-baselines the form after save without re-fetching the product (no follow-up GET).
      if (tabSwitch) activateTab(tabSwitch)
    } catch (e) {
      showProductApiError(e)
      if (productData) {
        form.reset({
          name: String(productData.name ?? ''),
          description: String(productData.description ?? ''),
          long_description: String(productData.long_description ?? ''),
          allow_dynamic_access_fee: to01(productData.allow_dynamic_access_fee) as 0 | 1,
          is_active: to01(productData.is_active) as 0 | 1,
        })
        setBaseline(
          snapshotFromValues({
            name: String(productData.name ?? ''),
            description: String(productData.description ?? ''),
            long_description: String(productData.long_description ?? ''),
            allow_dynamic_access_fee: to01(productData.allow_dynamic_access_fee) as 0 | 1,
            is_active: to01(productData.is_active) as 0 | 1,
          }),
        )
      }
    }
  },
  () => {
    pendingTabAfterSaveRef.current = null
    // Angular scrollToFirstError: center the first invalid field on invalid submit.
    document.getElementById('product_edit_name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  },
  )

  const onTopSubmit = async () => {
    if (!canEdit || submittingRef.current) return
    if (selectedTab === 'risklevel') {
      if (riskUi.locked) {
        showInfo('Risk Level is locked. Click Unlock to edit and save.')
        return
      }
      if (!riskUi.dirty) {
        showInfo('No changes to save.')
        return
      }
      beginSubmit()
      try {
        await riskRef.current?.save()
      } finally {
        endSubmit()
      }
      return
    }
    if (selectedTab === 'communicationtemplates') {
      if (!commRef.current) return
      beginSubmit()
      try {
        await commRef.current.save()
      } finally {
        endSubmit()
      }
      return
    }
    if (!basicDirty) {
      showInfo('No changes to save.')
      return
    }
    beginSubmit()
    try {
      await submitBasicInfo()
    } finally {
      endSubmit()
    }
  }

  const feeVal = form.watch('allow_dynamic_access_fee')
  useEffect(() => {
    if (isBase || lastFeeRef.current === null) return
    const n = to01(feeVal)
    if (n !== lastFeeRef.current) {
      if (selectedTab === 'risklevel' && n === 0) setSelectedTab('basicinfo')
    }
    lastFeeRef.current = n
  }, [feeVal, isBase, selectedTab])

  useEffect(() => {
    if (!canEdit) {
      navigate('/product', { replace: true })
    }
  }, [canEdit, navigate])

  useEffect(() => {
    // Keep the Communication Templates pane mounted once opened (Angular keeps it in the DOM, toggling d-none).
    if (selectedTab === 'communicationtemplates') setCommMounted(true)
  }, [selectedTab])

  if (!canEdit) return null

  const panelTitle =
    selectedTab === 'basicinfo'
      ? 'Basic info'
      : selectedTab === 'risklevel'
        ? 'Risk Level'
        : 'Communication templates'

  const panelSubtitle =
    selectedTab === 'basicinfo'
      ? 'Update the product details shown to users and administrators.'
      : selectedTab === 'risklevel'
        ? 'Define and manage risk levels and access fee percentages.'
        : 'Select one workflow and map an email template to it.'

  return (
    <div className="w-full min-h-screen bg-slate-50/50" data-testid="product-edit-page">
      <PageHeader title="Edit product" testIdRoot="product-edit-header" />

      <div className="mx-4 my-4 sm:mx-6 sm:my-5">
        {loading ? (
          <div className="flex justify-center py-20 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Header actions */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900">
                  Edit product
                </h1>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
                <Link
                  to="/product"
                  className={styles.btnSecondary}
                  data-testid="product-edit-cancel"
                >
                  Cancel
                </Link>
                <button
                  type="button"
                  onClick={() => void onTopSubmit()}
                  disabled={
                    submitting ||
                    loading ||
                    (selectedTab === 'basicinfo' && !basicDirty) ||
                    // Angular CS2NEW-1463: Submit disabled when locked or no dirty changes.
                    (selectedTab === 'risklevel' && (riskUi.locked || !riskUi.dirty)) ||
                    (selectedTab === 'communicationtemplates' && !commDirty)
                  }
                  className={styles.btnPrimary}
                  data-testid="product-edit-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save 
                </button>
              </div>
            </div>

            {/* Tabs — same EntityEditTabBar pattern as Company / Property edit */}
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <EntityEditTabBar
                navTestId="product-edit-tab-nav"
                ariaLabel="Product edit sections"
                tabs={productEditTabs}
                activeTab={selectedTab}
                onSelect={(id) => requestTab(id as Tab)}
              />
            </div>

            {/* Panel title */}
            {selectedTab !== 'communicationtemplates' && (
              <div>
                <h2 className="m-0 text-lg font-bold text-slate-900">{panelTitle}</h2>
                <p className="mt-1 mb-0 text-sm text-slate-500">{panelSubtitle}</p>
              </div>
            )}

            {/* Tab Panels */}
            {selectedTab === 'basicinfo' && (
              <form
                className={styles.formCard}
                onSubmit={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  // Angular edit: Enter in a single-line field submits/saves the basic-info form.
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                    e.preventDefault()
                    void onTopSubmit()
                  }
                }}
              >
                <div className={styles.sectionBlock} data-testid="product-edit-section-basic-info">
                  <div>
                    <h2 className={styles.sectionTitle} data-testid="product-edit-section-basic-info-title">
                      Product information
                    </h2>
                    <p className={styles.sectionSubtitle}>
                      Enter the product details shown to users and administrators.
                    </p>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="product_edit_name" className={styles.formLabel}>
                      Product name<span className={styles.requiredStar}>*</span>
                      {isBase && (
                        <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          Default
                        </span>
                      )}
                    </label>
                    <input
                      id="product_edit_name"
                      type="text"
                      className={cn(
                        styles.customFormControl,
                        form.formState.errors.name && styles.customFormControlError,
                      )}
                      {...form.register('name')}
                      placeholder="Enter product name"
                      data-testid="product-edit-name"
                    />
                    {form.formState.errors.name && (
                      <div className={styles.errorMessage}>
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        {form.formState.errors.name.message}
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="product_edit_description" className={styles.formLabel}>
                      Short description
                    </label>
                    <textarea
                      id="product_edit_description"
                      rows={4}
                      className={styles.customFormControl}
                      {...form.register('description')}
                      placeholder="Enter product description"
                      data-testid="product-edit-description"
                    />
                    <div className={styles.charCounter} aria-live="polite">
                      {descriptionLen} / {DESCRIPTION_MAX}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <span className={styles.formLabel}>Long description</span>
                    <Controller
                      name="long_description"
                      control={form.control}
                      render={({ field }) => (
                        <CKEditor4
                          id="product-edit-long-desc"
                          value={field.value || ''}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>

                {!isBase && (
                  <>
                    <hr className={styles.sectionDivider} />
                    <div className={styles.sectionBlock} data-testid="product-edit-section-settings">
                      <div>
                        <h2 className={styles.sectionTitle} data-testid="product-edit-section-settings-title">
                          Configuration
                        </h2>
                      </div>

                      <div className="flex flex-col gap-3">
                        <Controller
                          name="allow_dynamic_access_fee"
                          control={form.control}
                          render={({ field }) => (
                            <div className={styles.configRow}>
                              <div className={styles.configRowText}>
                                <p className={styles.configRowTitle}>Dynamic access fee</p>
                                <p className={styles.configRowDesc}>
                                  Allow the access fee to be configured dynamically.
                                </p>
                              </div>
                              <label className={styles.toggleLabel}>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={field.value === 1}
                                  onClick={() => field.onChange(field.value === 1 ? 0 : 1)}
                                  className={cn(
                                    styles.toggleTrack,
                                    field.value === 1 ? styles.toggleTrackOn : styles.toggleTrackOff,
                                  )}
                                  data-testid="product-edit-allow-dynamic-access-fee-toggle"
                                >
                                  <span
                                    className={cn(
                                      styles.toggleThumb,
                                      field.value === 1 ? styles.toggleThumbOn : styles.toggleThumbOff,
                                    )}
                                  />
                                </button>
                                <span
                                  className={cn(
                                    styles.toggleText,
                                    field.value === 1 && styles.toggleTextOn,
                                  )}
                                >
                                  {field.value === 1 ? 'On' : 'Off'}
                                </span>
                              </label>
                            </div>
                          )}
                        />

                        <Controller
                          name="is_active"
                          control={form.control}
                          render={({ field }) => (
                            <div className={styles.configRow}>
                              <div className={styles.configRowText}>
                                <p className={styles.configRowTitle}>Product status</p>
                                <p className={styles.configRowDesc}>
                                  Make this product available for use.
                                </p>
                              </div>
                              <label className={styles.toggleLabel}>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={field.value === 1}
                                  onClick={() => field.onChange(field.value === 1 ? 0 : 1)}
                                  className={cn(
                                    styles.toggleTrack,
                                    field.value === 1 ? styles.toggleTrackOnGreen : styles.toggleTrackOff,
                                  )}
                                  data-testid="product-edit-status-toggle"
                                >
                                  <span
                                    className={cn(
                                      styles.toggleThumb,
                                      field.value === 1 ? styles.toggleThumbOn : styles.toggleThumbOff,
                                    )}
                                  />
                                </button>
                                <span
                                  className={cn(
                                    styles.toggleText,
                                    field.value === 1 && styles.toggleTextOn,
                                  )}
                                >
                                  {field.value === 1 ? 'Active' : 'Inactive'}
                                </span>
                              </label>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* Risk Level stays mounted once shown; only hidden when inactive (Angular d-none, M-09). */}
            {canShowRisk && !Number.isNaN(productId) && (
              <div className={cn('min-w-0', selectedTab !== 'risklevel' && 'hidden')}>
                <ProductRiskLevelTab
                  ref={riskRef}
                  productId={productId}
                  onError={handleRiskError}
                  onUiState={setRiskUi}
                />
              </div>
            )}

            {/* Communication Templates: mounted on first open, then kept mounted and only hidden (M-09). */}
            {canShowComm && !Number.isNaN(productId) && commMounted && (
              <div className={cn('min-w-0', selectedTab !== 'communicationtemplates' && 'hidden')}>
                <ProductCommunicationTemplatesTab
                  key={`${productId}-${feeVal}`}
                  ref={commRef}
                  productId={productId}
                  onError={handleCommError}
                  onDirtyChange={setCommDirty}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={tabDialog.open}
        title="Unsaved changes"
        message=""
        htmlMessage={`You have unsaved changes in <strong>${tabLabel(selectedTab)}</strong>. Save changes before switching tabs?`}
        primaryLabel="Save"
        onPrimary={() => {
          const target = tabDialog.target
          setTabDialog({ open: false, target: null })
          if (target) void saveCurrentAndGo(target)
        }}
        secondaryLabel="Cancel changes"
        onSecondary={() => {
          const target = tabDialog.target
          discardCurrentTab()
          setTabDialog({ open: false, target: null })
          if (target) activateTab(target)
        }}
        onDismiss={() => setTabDialog({ open: false, target: null })}
        variant="warning"
        {...productConfirmPresentation}
      />
    </div>
  )
}
